import { FolderOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { uc5, type UC5Job } from "../../lib/api";
import type { Me } from "../../lib/me";
import { CASE_FINDINGS, caseBlock, existingCase, openCase, type CaseState } from "../../pages/vendorFlow";
import { stateLabel } from "../register/caseFlow";
import { FeatureOff } from "../FeatureOff";
import { RoleButton } from "../RequireRole";

/** Where the case on this finding stands, in words that never call a closed case open. */
function caseSentence(c: { tag: string; package: string; state: string }): string {
  if (c.state === "open") return `A case is open for ${c.tag} in ${c.package}.`;
  if (c.state === "closed") return `The case for ${c.tag} in ${c.package} is closed.`;
  return `The case for ${c.tag} in ${c.package} is at ${stateLabel(c.state)}.`;
}

/**
 * Open an obsolescence case on this finding, or follow the link to the one already open. Shown
 * only for an obsolete or announced finding; the case keeps the package, tag, register entry and
 * quoted source.
 */
export function OpenCaseView({ job, me, state, onOpen, onRecheck }: {
  job: UC5Job; me: Me | null | undefined; state: CaseState; onOpen: () => void;
  /** Start a new check from the same extraction, on the values the register now holds. */
  onRecheck?: () => void;
}) {
  if (!job.obsolescence || !CASE_FINDINGS.includes(job.obsolescence.status)) return null;
  if (state.kind === "off") return <div style={{ marginTop: 14 }}><FeatureOff feature="Obsolescence cases" reason={state.reason} /></div>;
  if (state.kind === "opened") {
    return (
      <p className="row" style={{ marginTop: 14, gap: 8, flexWrap: "wrap", fontSize: 14 }}>
        <FolderOpen size={16} aria-hidden="true" />
        <span>{caseSentence(state.case)}</span>
        <Link to={`/cases/${state.case.id}`}>Go to the case</Link>
      </p>
    );
  }
  if (state.kind === "stale") {
    return (
      <div style={{ marginTop: 14 }} role="alert">
        <p style={{ marginBottom: 8 }}>
          A case was not opened: {state.message}.
        </p>
        <RoleButton role="engineer" me={me} onClick={onRecheck} disabled={!onRecheck}>
          Check the current entry
        </RoleButton>
      </div>
    );
  }
  const block = caseBlock(job);
  return (
    <div style={{ marginTop: 14 }}>
      {state.kind === "error" && (
        <p role="alert" style={{ marginBottom: 8 }}><span className="pill fail">error</span> {state.message}</p>
      )}
      <RoleButton role="engineer" me={me} className="ghost" onClick={onOpen}
                  disabled={state.kind === "checking" || block !== null} title={block ?? undefined}>
        <span className="row"><FolderOpen size={16} /> Open a case</span>
      </RoleButton>
      {block && <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>{block}</p>}
    </div>
  );
}

/** The case action with its calls. Render with `key={job.id}`. */
export function OpenCase({ job, me }: { job: UC5Job; me: Me | null | undefined }) {
  const navigate = useNavigate();
  const [state, setState] = useState<CaseState>({ kind: "checking" });
  const qualifies = Boolean(job.obsolescence && CASE_FINDINGS.includes(job.obsolescence.status));
  useEffect(() => {
    if (!qualifies) return;
    let live = true;
    existingCase(job.id).then((s) => { if (live) setState(s); });
    return () => { live = false; };
  }, [job.id, qualifies]);

  async function open() {
    setState({ kind: "checking" });
    try { setState(await openCase(job.id)); } catch (e) { setState({ kind: "error", message: (e as Error).message }); }
  }
  async function recheck() {
    if (!job.source_job_id) return;
    setState({ kind: "checking" });
    try {
      const fresh = await uc5.fromJob(job.source_job_id);
      navigate(`/vendor/${encodeURIComponent(fresh.id)}`);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }
  return <OpenCaseView job={job} me={me} state={state} onOpen={open}
                       onRecheck={job.source_job_id ? () => void recheck() : undefined} />;
}
